(() => {
  const ACCESS_KEY = 'snf_wholesale_access';
  const PRODUCT_CODE_KEY = 'snf_wholesale_product_code';
  const SHIPPING_CODE_KEY = 'snf_wholesale_shipping_code';
  const ACTIVE_CLASS = 'snf-wholesale-active';
  const CART_PATH = '/cart';
  const CHECKOUT_PATH = '/checkout';
  const LOGIN_ANCHOR = '#snf-wholesale-password-entry';
  const LOGIN_PATH = '/pages/wholesale-on-printed-garments' + LOGIN_ANCHOR;
  const FEE_VARIANTS = {
    10000: 54090797187372,
    1000: 54090798891308,
    100: 54090798924076,
    10: 54090798956844,
    1: 54090798989612
  };
  const PAYMENT_LABELS = {
    e_transfer: 'Interac e-Transfer',
    credit_card: 'Credit card — outside Quebec',
    credit_card_quebec: 'Credit card — Quebec address'
  };
  let checkoutInFlight = false;
  let observerTimer = 0;

  const isActive = () => document.documentElement.classList.contains(ACTIVE_CLASS);

  function readStoredAccess() {
    try {
      return window.localStorage.getItem(ACCESS_KEY) === '1';
    } catch (_error) {
      return document.cookie.split('; ').some((part) => part === 'snf_wholesale=1');
    }
  }

  function setActive(active) {
    document.documentElement.classList.toggle(ACTIVE_CLASS, active);
    try {
      if (active) {
        window.localStorage.setItem(ACCESS_KEY, '1');
      } else {
        window.localStorage.removeItem(ACCESS_KEY);
      }
    } catch (_error) {
      // The cookie remains the fallback when storage is unavailable.
    }

    document.cookie = active
      ? 'snf_wholesale=1; Path=/; Max-Age=28800; SameSite=Lax; Secure'
      : 'snf_wholesale=; Path=/; Max-Age=0; SameSite=Lax; Secure';

    document.dispatchEvent(new CustomEvent('snf:wholesale-change', {
      detail: { active }
    }));
  }

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key) || '';
    } catch (_error) {
      return '';
    }
  }

  function sessionSet(key, value) {
    try {
      if (value) {
        window.sessionStorage.setItem(key, value);
      } else {
        window.sessionStorage.removeItem(key);
      }
    } catch (_error) {
      // Discount persistence still works through Shopify's cart.
    }
  }

  async function sha256(value) {
    const bytes = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)]
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  async function updateCart(payload) {
    const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  }

  async function applyWholesaleDiscounts(productCode, shippingCode, attributes) {
    const codes = [productCode, shippingCode].filter(Boolean);
    const payload = {};
    if (codes.length) payload.discount = codes.join(',');
    if (attributes) payload.attributes = attributes;
    return updateCart(payload);
  }

  function injectWholesaleBar() {
    const existing = document.querySelector('[data-snf-wholesale-bar]');
    if (!isActive()) {
      existing?.remove();
      return;
    }
    if (existing || !document.body) return;

    const bar = document.createElement('div');
    bar.className = 'snf-wholesale-bar';
    bar.dataset.snfWholesaleBar = '';
    bar.setAttribute('role', 'status');
    bar.innerHTML = '<span>Wholesale garment pricing active for eligible printed and embroidered garments</span><button type="button" data-snf-wholesale-exit>Exit wholesale</button>';
    document.body.prepend(bar);
  }

  function focusNotice(notice) {
    if (!notice) return;
    notice.classList.add('snf-wholesale-checkout--attention');
    notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
    notice.focus({ preventScroll: true });
    window.setTimeout(() => notice.classList.remove('snf-wholesale-checkout--attention'), 1800);
  }

  function pageCheckoutNotice() {
    return document.querySelector('[data-snf-wholesale-checkout][data-snf-wholesale-context="page"]');
  }

  function checkoutState() {
    const notice = pageCheckoutNotice();
    const ready = notice?.dataset.snfWholesaleReady === 'true';
    const acknowledgementRequired = notice?.dataset.snfWholesaleAckRequired === 'true';
    const acknowledgement = notice?.querySelector('[data-snf-wholesale-ack]');
    const payment = notice?.querySelector('[data-snf-wholesale-payment]:checked');
    const paymentMethod = payment?.value || '';
    const expectedFee = Number(notice?.dataset.snfWholesaleExpectedFee || 0);
    const feeTotal = Number(notice?.dataset.snfWholesaleFeeTotal || 0);
    const feeRequired = paymentMethod === 'credit_card';
    const feeReady = Boolean(paymentMethod)
      && feeTotal === (feeRequired ? expectedFee : 0);
    return {
      notice,
      ready,
      acknowledgementRequired,
      acknowledged: !acknowledgementRequired || Boolean(acknowledgement?.checked),
      freeShipping: notice?.dataset.snfWholesaleFreeShipping === 'true',
      paymentMethod,
      expectedFee,
      feeReady
    };
  }

  function syncCheckoutControls() {
    if (!isActive()) return;
    const state = checkoutState();
    const enabled = state.ready && state.acknowledged && Boolean(state.paymentMethod);
    document.querySelectorAll('button[name="checkout"][form="cart"], input[name="checkout"][form="cart"], #checkout').forEach((control) => {
      control.disabled = !enabled;
      control.setAttribute('aria-disabled', String(!enabled));
      if (!enabled) {
        if (!state.ready) {
          control.title = 'The $250 discounted wholesale minimum has not been reached.';
        } else if (!state.paymentMethod) {
          control.title = state.notice?.dataset.snfI18nSelectPayment || 'Choose a payment method before checkout.';
        } else if (!state.feeReady) {
          control.title = state.notice?.dataset.snfI18nFeeSync || 'The credit-card fee needs to be updated before checkout.';
        } else {
          control.title = state.notice?.dataset.snfI18nAcknowledge || 'Acknowledge the wholesale terms before checkout.';
        }
      } else {
        control.removeAttribute('title');
      }
    });

    document.querySelectorAll('cart-drawer .cart__ctas').forEach((container) => {
      if (container.querySelector('[data-snf-wholesale-review-cart]')) return;
      const link = document.createElement('a');
      link.href = CART_PATH;
      link.className = 'button button--primary button--full-width snf-wholesale-review-cart';
      link.dataset.snfWholesaleReviewCart = '';
      link.textContent = 'Review wholesale cart';
      container.append(link);
    });
  }

  function wholesaleDiscountApplied(cart, productCode) {
    const expected = String(productCode || '').trim().toLowerCase();
    if (!expected || !Array.isArray(cart?.discount_codes)) return false;
    return cart.discount_codes.some((discount) =>
      String(discount?.code || '').trim().toLowerCase() === expected
      && discount.applicable === true
    );
  }

  function feeUpdates(feeCents) {
    let remaining = Math.max(0, Math.round(feeCents));
    const updates = {};
    for (const denomination of [10000, 1000, 100, 10, 1]) {
      const variantId = FEE_VARIANTS[denomination];
      const quantity = Math.floor(remaining / denomination);
      updates[String(variantId)] = quantity;
      remaining -= quantity * denomination;
    }
    return updates;
  }

  function feeTotalFromCart(cart) {
    const feeIds = new Set(Object.values(FEE_VARIANTS).map(String));
    return (cart.items || []).reduce((total, item) => {
      if (!feeIds.has(String(item.variant_id))) return total;
      return total + Number(item.final_line_price || item.line_price || 0);
    }, 0);
  }

  function paymentAttributes(paymentMethod, expectedFee, accepted) {
    const label = PAYMENT_LABELS[paymentMethod] || '';
    const surcharge = paymentMethod === 'credit_card'
      ? `${(expectedFee / 100).toFixed(2)} CAD — 2.4% of discounted wholesale garment subtotal`
      : 'Not applicable';
    return {
      'Wholesale declared payment method': label,
      'Wholesale card surcharge': surcharge,
      'Wholesale checkout terms acknowledgement': accepted ? 'Accepted before checkout' : '',
      'Wholesale payment review': 'Compare declared choice with payment gateway before fulfillment'
    };
  }

  async function updatePaymentSelection(notice, paymentMethod, options = {}) {
    const expectedFee = Number(notice?.dataset.snfWholesaleExpectedFee || 0);
    const appliedFee = paymentMethod === 'credit_card' ? expectedFee : 0;
    const payload = {
      updates: feeUpdates(appliedFee),
      attributes: paymentAttributes(paymentMethod, expectedFee, Boolean(options.accepted))
    };
    const cart = await updateCart(payload);
    if (options.reload !== false) window.location.reload();
    return cart;
  }

  async function finishWholesaleCheckout() {
    if (checkoutInFlight) return;

    try {
      if (!window.SNFShippingCarriers) throw new Error('Please refresh the page before checkout.');
      const shippingCheck = await window.SNFShippingCarriers.normalize(true);
      if (shippingCheck.changed) {
        window.location.assign(CART_PATH);
        return;
      }
    } catch (error) {
      window.alert(error.message || 'Your cart could not be checked. Please try again.');
      return;
    }

    const state = checkoutState();
    if (!state.ready || !state.paymentMethod || !state.acknowledged) {
      focusNotice(state.notice);
      return;
    }

    const productCode = sessionGet(PRODUCT_CODE_KEY);
    const shippingCode = sessionGet(SHIPPING_CODE_KEY);
    if (!productCode) {
      focusNotice(state.notice);
      window.alert('Please return to the wholesale page and enter the password again before checkout.');
      window.location.assign(LOGIN_PATH);
      return;
    }

    checkoutInFlight = true;
    const acknowledgement = state.freeShipping
      ? 'Not required — FREE SHIPPING threshold reached'
      : 'Acknowledged — shipping determined after packing and invoiced before dispatch';

    try {
      const preparedCart = await updatePaymentSelection(state.notice, state.paymentMethod, {
        accepted: true,
        reload: false
      });
      const requiredFee = state.paymentMethod === 'credit_card' ? state.expectedFee : 0;
      if (feeTotalFromCart(preparedCart) !== requiredFee) {
        throw new Error(state.notice?.dataset.snfI18nFeeSync || 'The credit-card fee could not be verified.');
      }

      const cart = await applyWholesaleDiscounts(productCode, shippingCode, {
        'Wholesale order': 'Yes',
        'Wholesale shipping acknowledgement': acknowledgement,
        ...paymentAttributes(state.paymentMethod, state.expectedFee, true)
      });
      const discountApplied = wholesaleDiscountApplied(cart, productCode);

      if (!discountApplied) {
        throw new Error('Wholesale pricing is not active on this cart.');
      }

      window.location.assign(CHECKOUT_PATH);
    } catch (error) {
      checkoutInFlight = false;
      focusNotice(state.notice);
      window.alert(error?.message || 'Wholesale checkout could not be prepared. Please try again.');
    }
  }

  async function exitWholesale() {
    document.querySelectorAll('[data-snf-wholesale-exit]').forEach((button) => {
      button.disabled = true;
    });
    try {
      if (!window.SNFShippingCarriers) throw new Error('Please refresh the page before continuing.');
      await window.SNFShippingCarriers.normalize(false);
      await updateCart({
        discount: '',
        updates: feeUpdates(0),
        attributes: {
          'Wholesale order': '',
          'Wholesale shipping acknowledgement': '',
          'Wholesale declared payment method': '',
          'Wholesale card surcharge': '',
          'Wholesale checkout terms acknowledgement': '',
          'Wholesale payment review': ''
        }
      });
    } catch (error) {
      window.alert(error.message || 'Your cart could not be updated. Please try again.');
      document.querySelectorAll('[data-snf-wholesale-exit]').forEach(button => { button.disabled = false; });
      return;
    }
    sessionSet(PRODUCT_CODE_KEY, '');
    sessionSet(SHIPPING_CODE_KEY, '');
    setActive(false);
    window.location.reload();
  }

  function syncLoginAccess(focusField = false) {
    const reenter = window.location.hash === LOGIN_ANCHOR
      || (isActive() && !sessionGet(PRODUCT_CODE_KEY));
    document.querySelectorAll('[data-snf-wholesale-gate]').forEach((gate) => {
      gate.classList.toggle('snf-wholesale-ordering--reauth', reenter);
      if (reenter && focusField) {
        gate.querySelector('[data-snf-wholesale-password]')?.focus({ preventScroll: true });
      }
    });
  }

  function bindLoginForms() {
    syncLoginAccess();
    document.querySelectorAll('[data-snf-wholesale-login]').forEach((form) => {
      if (form.dataset.snfBound === 'true') return;
      form.dataset.snfBound = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const gate = form.closest('[data-snf-wholesale-gate]');
        const field = form.querySelector('[data-snf-wholesale-password]');
        const status = form.querySelector('[data-snf-wholesale-status]');
        const button = form.querySelector('button[type="submit"]');
        const password = String(field?.value || '').trim().toUpperCase();
        const expectedHash = String(gate?.dataset.snfPasswordHash || '').trim().toLowerCase();
        const shippingCode = String(gate?.dataset.snfShippingCode || '').trim();

        if (!password) {
          if (status) status.textContent = 'Enter the wholesale password.';
          field?.focus();
          return;
        }

        button.disabled = true;
        if (status) status.textContent = 'Checking wholesale access…';

        try {
          if (!window.crypto?.subtle || await sha256(password) !== expectedHash) {
            throw new Error('That wholesale password is not correct.');
          }

          if (!window.SNFShippingCarriers) throw new Error('Please refresh the page before continuing.');
          await window.SNFShippingCarriers.normalize(true);
          await applyWholesaleDiscounts(password, shippingCode);
          sessionSet(PRODUCT_CODE_KEY, password);
          sessionSet(SHIPPING_CODE_KEY, shippingCode);
          setActive(true);
          if (window.location.hash === LOGIN_ANCHOR) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
          if (status) status.textContent = 'Wholesale pricing unlocked.';
          injectWholesaleBar();
          window.location.reload();
        } catch (error) {
          setActive(false);
          if (status) status.textContent = error?.message || 'Wholesale access could not be unlocked.';
          field?.select();
          button.disabled = false;
        }
      });
    });
  }

  function eventTargetsCheckout(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return null;
    return target.closest(
      'button[name="checkout"], input[name="checkout"], a[href^="/checkout"], button[data-checkout]'
    );
  }

  document.addEventListener('click', (event) => {
    const exit = event.target instanceof Element
      ? event.target.closest('[data-snf-wholesale-exit]')
      : null;
    if (exit) {
      event.preventDefault();
      exitWholesale();
      return;
    }

    if (!isActive()) return;
    const refresh = event.target instanceof Element
      ? event.target.closest('[data-snf-wholesale-refresh]')
      : null;
    if (refresh) {
      event.preventDefault();
      window.location.reload();
      return;
    }
    const checkout = eventTargetsCheckout(event);
    if (!checkout) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const onFullCart = Boolean(checkout.closest('#main-cart-footer, form#cart'))
      || checkout.getAttribute('form') === 'cart';
    if (!onFullCart) {
      window.location.assign(CART_PATH);
      return;
    }

    finishWholesaleCheckout();
  }, true);

  document.addEventListener('submit', (event) => {
    if (!isActive()) return;
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;
    const submitter = event.submitter;
    const isCheckout = submitter?.getAttribute('name') === 'checkout'
      || form.matches('#cart-notification-form')
      || form.action.includes('/checkout');
    if (!isCheckout) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.id !== 'cart') {
      window.location.assign(CART_PATH);
      return;
    }
    finishWholesaleCheckout();
  }, true);

  document.addEventListener('change', async (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches('[data-snf-wholesale-payment]')) {
      const notice = event.target.closest('[data-snf-wholesale-checkout]');
      const status = notice?.querySelector('[data-snf-wholesale-payment-status]');
      notice?.querySelectorAll('[data-snf-wholesale-payment]').forEach((input) => {
        input.disabled = true;
      });
      if (status) status.textContent = notice?.dataset.snfI18nUpdating || 'Updating the payment selection and fee…';
      try {
        await updatePaymentSelection(notice, event.target.value);
      } catch (_error) {
        notice?.querySelectorAll('[data-snf-wholesale-payment]').forEach((input) => {
          input.disabled = false;
        });
        if (status) status.textContent = notice?.dataset.snfI18nUpdateFailed || 'The payment selection could not be updated.';
      }
      return;
    }
    if (event.target.matches('[data-snf-wholesale-ack]')) {
      syncCheckoutControls();
    }
  });

  const observer = new MutationObserver(() => {
    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(() => {
      bindLoginForms();
      injectWholesaleBar();
      syncCheckoutControls();
    }, 60);
  });

  if (readStoredAccess()) {
    document.documentElement.classList.add(ACTIVE_CLASS);
  }

  const initialize = () => {
    bindLoginForms();
    if (window.location.hash === LOGIN_ANCHOR) syncLoginAccess(true);
    injectWholesaleBar();
    syncCheckoutControls();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  window.addEventListener('hashchange', () => syncLoginAccess(true));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();

